import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import VDRListTable from '../components/VDR/VDRListTable';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function VDR() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [manageRoomId, setManageRoomId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Get user context
  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      const response = await fetch(`${API_URL}/vdr`, {
        headers: {
          'x-user-id': userId,
          'x-user-role': userRole
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Ensure data is always an array
        setRooms(Array.isArray(data) ? data : []);
      } else {
        setError('Failed to load VDR rooms');
      }
    } catch (e) {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (data: any) => {
    try {
      const response = await fetch(`${API_URL}/vdr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': userRole
        },
        body: JSON.stringify({ ...data, createdBy: userId })
      });

      if (response.ok) {
        setShowCreateModal(false);
        loadRooms();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to create room');
      }
    } catch (e) {
      alert('Failed to create room');
    }
  };

  if (loading) {
    return (
      <div className="min-h-full">
        <div className="mb-6 flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-semibold mb-2">Virtual Data Room</h1>
            <p className="text-neutral-500">Secure document rooms you have access to.</p>
          </div>
        </div>
        <div className="py-8 text-neutral-500">Loading VDR Rooms...</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Virtual Data Room</h1>
          <p className="text-neutral-500">Secure document rooms you have access to.</p>
        </div>
        {userRole === 'super_admin' && (
           <Button onClick={() => setShowCreateModal(true)} text="Create Room" />
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      <VDRListTable 
        rooms={rooms}
        userRole={userRole}
        onShare={(roomId) => setManageRoomId(roomId)}
        onSettings={(roomId) => navigate(`/fundraising/vdr/${roomId}/settings`)}
      />

      {showCreateModal && (
        <CreateRoomModal 
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateRoom}
        />
      )}

      {manageRoomId && (
        <ManageAccessModal 
          roomId={manageRoomId} 
          onClose={() => {
              setManageRoomId(null);
              loadRooms(); // Reload
          }} 
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// SUB-COMPONENTS
// ----------------------------------------------------------------------

function CreateRoomModal({ onClose, onSubmit }: { onClose: () => void, onSubmit: (data: any) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [accessCode, setAccessCode] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, description, slug, accessCode });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Create New VDR</h2>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-2">✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Room Name</label>
              <Input 
                value={name} 
                onChange={e => setName(e.target.value)} 
                required 
                placeholder="e.g. Project Apollo" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                placeholder="Internal description..." 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">URL Slug</label>
                <Input 
                  value={slug} 
                  onChange={e => setSlug(e.target.value)} 
                  placeholder="project-apollo" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Master Password</label>
                <Input 
                  value={accessCode} 
                  onChange={e => setAccessCode(e.target.value)} 
                  placeholder="Optional" 
                />
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={onClose} text="Cancel" />
            <Button type="submit" text="Create Room" />
          </div>
        </form>
      </div>
    </div>
  );
}

function ManageAccessModal({ roomId, onClose }: { roomId: string, onClose: () => void }) {
  const userRole = localStorage.getItem('app_role') || 'user';
  const userId = localStorage.getItem('app_user_id') || '';
  
  // Basic Room Info (for name)
  const [room, setRoom] = useState<any>(null);
  
  useEffect(() => {
    fetch(`${API_URL}/vdr/${roomId}`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
    }).then(res => res.json()).then(setRoom);
  }, [roomId]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-neutral-100">
           <div>
             <h2 className="text-xl font-bold">Share VDR</h2>
             <p className="text-sm text-neutral-500">{room?.name}</p>
           </div>
           <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-2">✕</button>
        </div>

        {/* Content */}
        <div className="p-6">
            <UserAccessTab roomId={roomId} />
        </div>

        <div className="p-6 border-t border-neutral-100 bg-neutral-50 flex justify-end rounded-b-lg">
          <Button variant="outline" onClick={onClose} text="Done" />
        </div>
      </div>
    </div>
  );
}

function UserAccessTab({ roomId }: { roomId: string }) {
  const [permissions, setPermissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [error, setError] = useState('');

  const userRole = localStorage.getItem('app_role') || 'user';
  const userId = localStorage.getItem('app_user_id') || '';

  useEffect(() => {
    loadPermissions();
  }, [roomId]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/vdr/${roomId}/permissions`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data);
      } else {
        setError('Failed to load permissions');
      }
    } catch (e) {
      setError('Error loading permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/vdr/${roomId}/permissions`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-user-role': userRole,
          'x-user-id': userId
        },
        body: JSON.stringify({ email, accessLevel: role })
      });
      
      if (response.ok) {
        setEmail('');
        loadPermissions();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to add user');
      }
    } catch (e) {
      alert('Failed to add user');
    }
  };

  const handleRemoveUser = async (targetUserId: string) => {
    if (!confirm('Remove access for this user?')) return;
    try {
      const response = await fetch(`${API_URL}/vdr/${roomId}/permissions/${targetUserId}`, {
        method: 'DELETE',
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      
      if (response.ok) {
        loadPermissions();
      } else {
        alert('Failed to remove user');
      }
    } catch (e) {
      alert('Failed to remove user');
    }
  };

  return (
    <div>
        {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded border border-red-200">
                {error}
            </div>
        )}
        <form onSubmit={handleAddUser} className="mb-8 p-4 bg-neutral-50 rounded border border-neutral-100">
          <h3 className="text-sm font-semibold mb-3 text-neutral-700">Add Team Member</h3>
          <div className="flex gap-2 items-center">
             <div className="flex-1">
                <Input 
                  type="email" 
                  placeholder="User Email" 
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
             </div>
             <div className="w-32">
                <Select 
                  value={role}
                  onChange={e => setRole(e.target.value)}
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </Select>
             </div>
             <Button type="submit" size="md" text="Add" />
          </div>
        </form>

        <h3 className="text-sm font-semibold mb-3 text-neutral-700">Team Access</h3>
        {loading ? (
            <div className="text-center py-4 text-neutral-400">Loading...</div>
        ) : (
        <div className="border rounded divide-y">
            {permissions.length === 0 ? (
            <div className="p-4 text-center text-neutral-400 text-sm">No specific permissions set.</div>
            ) : (
            permissions.map((perm) => (
                <div key={perm.id} className="p-3 flex justify-between items-center">
                <div>
                    <div className="font-medium text-sm">{perm.profiles?.name || 'Unknown User'}</div>
                    <div className="text-xs text-neutral-500">{perm.profiles?.email}</div>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs bg-neutral-100 px-2 py-1 rounded capitalize">{perm.access_level}</span>
                    <Button 
                        variant="danger-outline"
                        className="h-6 text-xs px-2"
                        onClick={() => handleRemoveUser(perm.user_id)}
                        text="Remove"
                    />
                </div>
                </div>
            ))
            )}
        </div>
        )}
    </div>
  );
}
